import React from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import PageLayout from '../components/common/PageLayout';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const { t } = useTranslation();
  const baseUrl = process.env.REACT_APP_SEO_BASE_URL || window.location.origin;

  return (
    <>
      <Helmet>
        <title>About Us | MWQQ.com - Privacy-First Passport Photo Tool</title>
        <meta name="description" content="Learn about MWQQ.com. We provide a privacy-first passport photo tool with AI processing that runs entirely in your browser. No uploads, complete privacy." />
        <meta name="keywords" content="about us, passport photo tool, privacy-first, AI photo processing, browser-based, local processing, mission" />
        <link rel="canonical" href={`${baseUrl}/about.html`} />
        <meta property="og:title" content="About Us | MWQQ.com" />
        <meta property="og:description" content="Privacy-first passport photo tool. AI processing in your browser." />
        <meta property="og:url" content={`${baseUrl}/about.html`} />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "MWQQ.com",
            "url": baseUrl,
            "logo": `${baseUrl}/logo.png`,
            "description": "Privacy-first passport photo creation tool with AI-powered browser-based processing",
            "email": "mwqq.com@gmail.com",
            "foundingDate": "2010",
            "sameAs": []
          })}
        </script>
      </Helmet>

      <PageLayout title={t('pages.about.title')}>
      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.about.sections.mission.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300">
          {t('pages.about.sections.mission.content')}
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.about.sections.whyPrivacy.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.about.sections.whyPrivacy.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.about.sections.whyPrivacy.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.about.sections.howItWorks.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.about.sections.howItWorks.content')}
        </p>
        <ul className="list-disc list-inside space-y-2 text-gray-700 dark:text-gray-300">
          {t('pages.about.sections.howItWorks.points', { returnObjects: true }).map((point: string, index: number) => (
            <li key={index}>{point}</li>
          ))}
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.about.sections.ourValues.title')}
        </h2>
        <div className="text-gray-700 dark:text-gray-300 whitespace-pre-line">
          {t('pages.about.sections.ourValues.content')}
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4">
          {t('pages.about.sections.contact.title')}
        </h2>
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {t('pages.about.sections.contact.content')}
        </p>
        <a
          href="mailto:mwqq.com@gmail.com"
          className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 text-lg"
        >
          mwqq.com@gmail.com
        </a>
      </section>

      <div className="mt-12 pt-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          <Link to="/contact.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.contact')}
          </Link>
          {' | '}
          <Link to="/faq.html" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
            {t('footer.links.faq')}
          </Link>
        </p>
      </div>
      </PageLayout>
    </>
  );
};

export default About;
